import React, { useRef, useState } from "react";
import { Avatar, Badge, Button, Divider, List, Popover, Tooltip, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";

import { handleSessionExpired } from '@/utils/api';
import { KIND_WORD, OBJECT_WORD, actsOf, myActionsApi, openMyAction } from '@/pages/quality/cpv/cpvMyActionsApi';
import { defaultColors } from '@/themes/antd-theme';
import { useResponsive } from '@/hooks/useResponsive';
import { useTheme } from '@/App';
import { formatDateTime } from '@/utils/datetime';
import useLiveNotifications from '@/hooks/useLiveNotifications';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

/** The live events that change what waits on the person (NotificationSocketContext). */
const LIVE_EVENTS = ['cpv_signal'];
/** The REST re-read while the socket is not connected. */
const POLL_MS = 30000;

const initials = (row) => String(KIND_WORD[row?.kind] || row?.kind || '?').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

/**
 * The bell: the CPV acts waiting on the signed-in person (GET /bp/cpv/my-actions/, the first page at the server's
 * `cpv.api.page_size`, overdue first). The badge is the server's total; a row opens the object's CPV page.
 */
const Notification = () => {
  const { isDark } = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  // Set on a 401 so subsequent ticks no-op instead of hitting a dead session.
  const stoppedRef = useRef(false);

  const load = () => {
    if (stoppedRef.current) return Promise.resolve();
    return myActionsApi.list()
      .then((d) => setData(d || null))
      .catch((err) => {
        if (err?.response?.status === 401) {
          stoppedRef.current = true;
          handleSessionExpired(err?.response?.data?.message);
        }
      });
  };

  const handleToggle = (newOpen) => {
    setOpen(newOpen);
    if (newOpen) load();
  };

  useLiveNotifications(load, LIVE_EVENTS, POLL_MS);

  const rows = data?.results || [];
  const total = Number(data?.count) || 0;

  const handleClick = (row) => {
    setOpen(false);
    openMyAction(row, navigate);
  };

  const renderItem = (row) => (
    <React.Fragment key={`${row.kind}-${row.object_type}-${row.object_id}`}>
      <List.Item
        className="py-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded"
        onClick={() => handleClick(row)}
        data-testid="bell-row"
      >
        <div className="flex w-full gap-3">
          <Avatar
            size={36}
            style={{
              color: row.overdue ? defaultColors.error : defaultColors.warning,
              backgroundColor: 'white',
              border: `1px solid ${row.overdue ? defaultColors.error : defaultColors.warning}`,
              flexShrink: 0,
            }}
          >
            {initials(row)}
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold mb-1" style={{ color: row.overdue ? defaultColors.error : defaultColors.warning }}>
              {KIND_WORD[row.kind] || row.kind}{row.overdue ? ' · Overdue' : ''}
            </div>
            <Typography.Paragraph ellipsis={{ rows: 2, expandable: false }} className="mb-1">
              {[OBJECT_WORD[row.object_type] || row.object_type, row.number, row.product].filter(Boolean).join(' · ')}
            </Typography.Paragraph>
            <div className="text-xs" style={{ color: defaultColors.text.secondary }}>
              {[actsOf(row), row.due_at ? `Due ${formatDateTime(row.due_at)}` : null].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      </List.Item>
      <Divider className="my-0" />
    </React.Fragment>
  );

  const popoverContent = (
    <div style={{ width: isMobile ? 285 : 420 }} data-testid="bell-popover">
      <div className="flex items-center gap-2 py-2 font-semibold">
        <Badge count={total} size="small" showZero={false}><BellOutlined style={{ color: defaultColors.warning }} /></Badge>
        <span>Waiting on you</span>
      </div>
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        <List dataSource={rows} renderItem={renderItem} locale={{ emptyText: 'Nothing is waiting for your action' }} />
      </div>
      <Divider className="my-1" />
      <div className="text-center py-2">
        <Button
          type="link"
          onClick={() => {
            navigate("/cpv/my-actions");
            setOpen(false);
          }}
        >
          <h6 className="text-lg font-semibold m-0" style={{ color: defaultColors.primary }}>
            View all
          </h6>
        </Button>
      </div>
    </div>
  );

  // Dark mode aware colors
  const bgColor = isDark
    ? (open ? defaultColors.grey[600] : defaultColors.grey[700])
    : (open ? defaultColors.grey[300] : defaultColors.grey[100]);
  const iconColor = isDark ? defaultColors.grey[100] : defaultColors.grey[700];

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleToggle}
      placement={isMobile ? "bottom" : "bottomRight"}
    >
      <Tooltip title="Notifications" zIndex={9999}>
        <Button
          type="text"
          shape="circle"
          aria-label="Notifications"
          data-testid="bell"
          icon={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Badge count={total} size="small">
                <BellOutlined style={{ fontSize: 23, display: 'block', color: iconColor }} />
              </Badge>
            </div>
          }
          style={{
            backgroundColor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        />
      </Tooltip>
    </Popover>
  );
};

export default Notification;
